
import type { SetupContext } from 'vue'
import type { Route } from 'vue-router'
import type { IToolingListComposableProps } from '../../../composables/use-tooling-list'
import type { DataTableColumn, DataTableOption, GuardResolveFunction } from '../../../models'
import type { Equalize } from '../../../modules/equalize'
import { computed, defineComponent, getCurrentInstance, ref } from 'vue'
import CrudList from '../../../components/crud/crud-list.component.vue'
import DataTable from '../../../components/crud/data-table.component.vue'
import { UiTooltip } from '../../../components/keen-ui'
import { useToolingList } from '../../../composables/use-tooling-list'
import { AlignEnum, EqualizeFeatureEnum, FeatureIdEnum } from '../../../enums'
import { ROUTER_CONSTS } from '../../../router/router.consts'
import { computedFn } from './computed'
import { methodsFn } from './methods'

export default defineComponent({
  name: 'EqualizeList',
  components: {
    CrudList,
    DataTable,
    UiTooltip,
  },
  async beforeRouteLeave(to: Route, from: Route, next: GuardResolveFunction) {
    this.contextWatchDisabled = true

    next()
  },
  setup(props: IToolingListComposableProps, ctx: SetupContext) {
    const {
      proxy: { $epb },
    } = getCurrentInstance()

    const baseClass = 'epb-equalize-list'

    const routes = ROUTER_CONSTS.routes[FeatureIdEnum.EQUALIZE]

    const messages = $epb.messages.tools.equalize

    const loadDataExtraParams = computed(() => ({ isDefault: false }))

    const toolingList = useToolingList<Equalize>(props, ctx, {
      routes,
      messages,
      loadDataExtraParams,
    })

    const features = Object.values(EqualizeFeatureEnum).filter(
      value =>
        value !== EqualizeFeatureEnum.SEMANTIC_MATCH_SEARCH &&
        value !== EqualizeFeatureEnum.SPONSORED_SCORE,
    )
    const equalizeListComputed = computedFn(props, ctx, { toolingList })
    const equalizeListMethods = methodsFn(props, ctx, { toolingList })

    const columns: Array<DataTableColumn> = [
      {
        id: 'title',
        sortable: true,
        text: messages.fields.title.label,
      },
      ...(equalizeListComputed.defaultRows.value.length > 1
        ? [
            {
              id: 'endpointId',
              text: messages.fields.endpointId.label,
            },
          ]
        : []),
      {
        id: 'id',
        text: messages.fields.id.label,
        copyToClipboard: true,
        width: '200%',
      },
      ...toolingList.visiblePivotColumns.value,
      {
        id: 'modified',
        text: messages.fields.modified.label,
        align: AlignEnum.CENTER,
      },
      {
        id: 'metadata.updated',
        sortable: true,
        sortableId: 'updated',
        text: messages.fields.updateDate.label,
      },
      {
        id: 'metadata.author',
        text: messages.fields.user.label,
      },
      {
        id: 'criteria.enabled',
        text: messages.fields.enabled.label,
      },
      {
        id: 'options',
        width: '44px',
        align: AlignEnum.CENTER,
      },
    ]

    toolingList.loadData()

    const crudListRef = ref(null)

    const onOptionsClick = (option: DataTableOption, dataItem): void => {
      toolingList.onOptionClick(option, dataItem, crudListRef)
    }

    return {
      ...toolingList,
      ...equalizeListComputed,
      ...equalizeListMethods,
      features,
      baseClass,
      routes,
      messages,
      columns,
      crudListRef,
      onOptionsClick,
    }
  },
})
